import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { UI2DateTimeFieldProps } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import { UI2_DATE_TIME_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import {
  formatDateTimeValue,
  getDefaultDateTimePlaceholder,
  isDateTimeValueEmpty,
} from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'

import { DateTimeInput } from 'browser/components/atomic-elements/atoms/input/date-time-input/date-time-input'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import './_ui2-date-time-field.scss'

import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'

interface IUI2DateTimeWebExtras {
  onChange?: (value: any) => void
  errors?: any[]
  className?: string
  daySize?: number
  isLoading?: boolean
}

/**
 * @uiComponent
 */
export interface IUI2DateTimeProps extends UI2DateTimeFieldProps, IUI2DateTimeWebExtras {}

export class UI2DateTime extends React.Component<IUI2DateTimeProps> {
  public static defaultProps: Partial<IUI2DateTimeProps> = {
    ...UI2_DATE_TIME_FIELD_DEFAULT_PROPS,
    minuteStep: 5, // web-specific: native date picker minute increment
  }

  public render() {
    const { className } = this.props
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-date-time-field', className)}
        isValueEmpty={isDateTimeValueEmpty}
        renderReadOnly={this.renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl className="ui2-date-time-field__control">
          {this.renderDateTime()}
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderDateTime(): React.ReactNode {
    const {
      value,
      onChange,
      mode = 'datetime',
      valueMode,
      showTimezoneInput,
      minDate,
      maxDate,
      daySize,
      placeholder,
      minuteStep,
      isDisabled,
      isClearable,
      label,
    } = this.props
    return (
      <DateTimeInput
        value={value}
        onChange={onChange ?? _.noop}
        mode={mode}
        valueMode={valueMode}
        showTimezoneInput={showTimezoneInput}
        isDisabled={isDisabled}
        isClearable={isClearable}
        placeholder={placeholder ?? getDefaultDateTimePlaceholder(mode, label)}
        minDate={minDate}
        maxDate={maxDate}
        daySize={daySize}
        minuteStep={minuteStep}
      />
    )
  }

  /**
   * The shell renders the empty state itself before ever calling this, so
   * there is no empty branch here. A value that won't parse falls back to its
   * raw string rather than reading as absent.
   */
  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    const { mode = 'datetime', showTimezoneInput } = this.props
    const formatted = formatDateTimeValue(value, mode, showTimezoneInput)
    const display = formatted || String(_.get(value, 'dateTime', value) ?? '')
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <div className='ui2-date-time-field--readOnly'>{display}</div>
        </div>
        {editNode}
      </>
    )
  }
}
